import { createTRPCReact } from "@trpc/react-query";
import { httpBatchLink } from "@trpc/client";
import superjson from "superjson";
import type { AppRouter } from "@ymonetize/api/routers";

export const trpc = createTRPCReact<AppRouter>();

function getApiUrl() {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return `${envUrl}/trpc`;
  return "/trpc"; // proxy in dev
}

function getAuthToken(): string | null {
  return localStorage.getItem("accessToken");
}

// Token refresh state to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  try {
    const apiUrl = getApiUrl();
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        "0": {
          json: {
            refreshToken,
          },
        },
      }),
    });

    // Use a direct fetch to the refresh endpoint instead of batched tRPC
    const directUrl = apiUrl.replace("/trpc", "");
    const refreshResponse = await fetch(`${directUrl}/trpc/auth.refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        json: { refreshToken },
      }),
    });

    if (!refreshResponse.ok) {
      throw new Error("Refresh failed");
    }

    const data = await refreshResponse.json();
    const result = data?.result?.data?.json;

    if (result?.accessToken && result?.refreshToken) {
      localStorage.setItem("accessToken", result.accessToken);
      localStorage.setItem("refreshToken", result.refreshToken);
      return result.accessToken;
    }

    throw new Error("Invalid refresh response");
  } catch {
    // Refresh failed — clear tokens and force re-login
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    // Dispatch a custom event so useAuth can react
    window.dispatchEvent(new CustomEvent("auth:logout"));
    return null;
  }
}

async function getValidToken(): Promise<string | null> {
  const token = getAuthToken();
  if (!token) return null;

  // Check if token is expired by decoding JWT payload
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiresAt = payload.exp * 1000;
    const now = Date.now();

    // If token expires in less than 60 seconds, proactively refresh
    if (expiresAt - now < 60_000) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken().finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
      }
      return refreshPromise;
    }
  } catch {
    // If we can't decode the token, just use it as-is
  }

  return token;
}

// Custom fetch that handles 401 with automatic token refresh
function createAuthFetch(): typeof fetch {
  return async (input, init) => {
    const response = await fetch(input, init);

    // If we get a 401 and have a refresh token, try to refresh
    if (response.status === 401 && localStorage.getItem("refreshToken")) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken().finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;
      if (newToken) {
        // Retry the original request with the new token
        const newInit = {
          ...init,
          headers: {
            ...Object.fromEntries(new Headers(init?.headers).entries()),
            Authorization: `Bearer ${newToken}`,
          },
        };
        return fetch(input, newInit);
      }
    }

    return response;
  };
}

export function createTrpcClient() {
  return trpc.createClient({
    links: [
      httpBatchLink({
        url: getApiUrl(),
        transformer: superjson,
        fetch: createAuthFetch(),
        async headers() {
          const token = await getValidToken();
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
      }),
    ],
  });
}
